import app from '../main.js'
/**
 * @fileoverview Helper functions for date picker formatting and localization
 * @author LiveLaps Development Team
 * @version 1.0.0
 * @copyright LiveLaps Inc. 2024
 * @developer Lorena Salas
 */

/**
 * Formats a datetime string into a specific format
 * @param {string} datetime - The datetime to format
 * @returns {string} Formatted date string (YYYY-MM-DD HH:mm:ss)
 */
export function getDate(datetime) {
  let fullDate = new Date(datetime);
  let date = fullDate.toLocaleDateString().split("/");
  let minutes = fullDate.toLocaleTimeString().slice(0, 7).split(":");

  let [month, day, year] = date;
  let [hour, minute, second] = minutes;

  if (month < 10) month = "0" + month;
  if (day < 10) day = "0" + day;
  if (hour < 10) hour = "0" + hour;

  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
}

/**
 * Capitalizes the first letter of a string
 * @param {string} string - The string to capitalize
 * @returns {string} Capitalized string
 */
export function capitalize(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * Parses a date string (YYYY, YYYY-MM, or YYYY-MM-DD) as local time instead of UTC,
 * preventing off-by-one day/month shifts in timezones behind UTC.
 */
function parseLocalDate(date) {
  if (typeof date === 'string') {
    if (/^\d{4}$/.test(date)) {
      return new Date(Number(date), 0, 1);
    }
    if (/^\d{4}-\d{2}$/.test(date)) {
      const [year, month] = date.split('-').map(Number);
      return new Date(year, month - 1, 1);
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
  }
  return new Date(date);
}

/**
 * Formats a date for the header display
 * @param {Date} date - The date to format
 * @param {string} locale - The locale to use for formatting
 * @returns {string} Formatted date string with month and year
 */
export function formatDateHeader(date, locale = app.$i18n.locale) {
  const options = { month: 'long', year: 'numeric' }
  return capitalize(parseLocalDate(date).toLocaleDateString(locale, options));
}

/**
 * Formats a date to show weekday
 * @param {Date} date - The date to format
 * @param {string} locale - The locale to use for formatting
 * @returns {string} Formatted weekday string
 */
export function formatWeekday(date, locale = app.$i18n.locale) {
  const options = { weekday: 'short' }
  return capitalize(parseLocalDate(date).toLocaleDateString(locale, options));
}

/**
 * Formats a date to show month
 * @param {Date} date - The date to format
 * @param {string} locale - The locale to use for formatting
 * @returns {string} Formatted month string
 */
export function formatMonth(date, locale = app.$i18n.locale) {
  const options = { month: 'short' }
  return capitalize(parseLocalDate(date).toLocaleDateString(locale, options));
}

/**
 * Formats a date to show weekday, month and day
 * @param {Date} date - The date to format
 * @param {string} locale - The locale to use for formatting
 * @returns {string} Formatted date string
 */
export function formatTitleDate(date, locale = app.$i18n.locale) {
  const options = { weekday: 'short', month: 'short', day: 'numeric' }
  return capitalize(parseLocalDate(date).toLocaleDateString(locale, options));
}

export default {
    install(Vue) {
        Vue.prototype.$datePicker = {
            getDate,
            capitalize,
            formatDateHeader,
            formatWeekday,
            formatMonth,
            formatTitleDate
        }
    }
}